import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import DOMPurify from 'isomorphic-dompurify'
import { useRouter } from 'next/router'

import { status as productStatus } from '@jarvis.oddle/core/tools/product'
import {
  margin,
  marginSizes,
  padding,
  paddingSizes,
  buttonRadiusValues,
  typo
} from '@jarvis.oddle/core/theme/default'
import { pixelise } from '@jarvis.oddle/core/tools/style'
import { formatDayRange } from '@jarvis.oddle/core/tools/date'
import Div from '@jarvis.oddle/core/components/Div'
import { H1, H4 } from '@jarvis.oddle/core/components/Typo'
import ExternalContent from '@jarvis.oddle/core/components/ExternalContent'
import {
  InlineNotification,
  notificationStates
} from '@jarvis.oddle/core/components/InlineNotification'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'

import { useAppContext } from 'src/containers/AppContext'
import useCustomisation from 'src/hooks/useCustomisation'
import { useStoreItemAvailability } from 'src/containers/StoreItemAvailability'
import { ADDTHIS_PUBLIC_ID } from 'config/client'
import {
  getAllProductNutrigradeTags,
  getDisplayPriceFromItem
} from 'src/utils/product'
import { imageSize } from './ProductImage'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import SocialMediaSharing from 'src/components/molecules/InlineSocialMediaSharing'
import { formatDate } from 'src/utils/date'
import { NutriGradeIcon } from 'src/components/icons/NutriGrade'
import { isEmpty } from 'src/utils/string'
import { isBrowser } from '@jarvis.oddle/core/env'

const Container = styled(Div)<{
  showImage: boolean
}>`
  padding: ${padding[paddingSizes.medium]};
  padding-bottom: 0;
  ${breakpoint('desktop')`
    ${(props) =>
      props.showImage
        ? `
      width: calc(100% - ${pixelise(imageSize)});
      padding-left: ${padding[paddingSizes.extraLarge]};
      `
        : `
      padding-left: ${padding[paddingSizes.medium]};
      `}
  `}
`

const ItemList = styled.p`
  display: list-item;
  margin: ${margin[marginSizes.extraSmall]};
  margin-left: ${margin[marginSizes.medium]};
`

const ItemListClickable = styled.p`
  cursor: pointer;
  text-decoration: underline;
`

const StyledProductName = styled(H1)`
  display: inline;
`

const StyledProductDescription = styled(H4)`
  display: flex;
  flex-direction: column;
  * {
    font-family: ${(props) =>
      props.fontFamily || props.theme.typographyHeadings};
  }
  ul {
    margin: 0;
  }
`

const ProductTagContainer = styled.div`
  padding: 10px 0;
  display: flex;
  flex-flow: wrap;
  grid-gap: 5px;
`

const ProductTag = styled.div`
  display: inline-block;
  background: white;
  padding: 4px 5px;
  border: 1px solid #bbb;
  color: black;
  font-family: 'Roboto', sans-serif;
  border-radius: ${(props) => buttonRadiusValues[props.theme.buttonShape]};
  font-size: ${typo.smallText.desktopFontSize};
`

const getSeasonalAvailabilityMessage = (
  t,
  category,
  product,
  router
): {
  text: string
  subText?: string[]
} => {
  const item = category.status === productStatus.AVAILABLE ? product : category
  const {
    collectionStartDate,
    collectionEndDate,
    collectionStartTime,
    collectionEndTime,
    availableDays
  } = item
  const formattedAvailableDays = availableDays
    ? formatDayRange(availableDays, 'short', t)
    : ''
  const isAlwaysAvailable =
    (availableDays || '').split(',').filter((x) => x).length === 7

  if (collectionStartDate && collectionEndDate) {
    return {
      text: t(
        'Only available for orders placed for _collectionStartDate_ to _collectionEndDate_',
        {
          collectionStartDate: formatDate({
            t,
            date: collectionStartDate,
            locale: (router?.query?.locale as string) || 'en_SG',
            options: { year: 'numeric', month: 'short', day: '2-digit' }
          }),
          collectionEndDate: formatDate({
            t,
            date: collectionEndDate,
            locale: (router?.query?.locale as string) || 'en_SG',
            options: { year: 'numeric', month: 'short', day: '2-digit' }
          })
        }
      )
    }
  }

  if (collectionStartTime && collectionEndTime) {
    return {
      text: availableDays
        ? t(
            'Only available for orders placed for _collectionStartTime_ to _collectionEndTime_ on _availableDays_',
            {
              collectionStartTime,
              collectionEndTime,
              availableDays: formattedAvailableDays
            }
          )
        : t(
            'Only available for orders placed for _collectionStartTime_ to _collectionEndTime_',
            item
          )
    }
  }

  if (!isAlwaysAvailable) {
    return {
      text: availableDays
        ? t('Only available on _availableDays_', {
            availableDays: formattedAvailableDays
          })
        : t('Sorry, this item is currently unavailable')
    }
  }
}

const getStoreItemAvailabilityMessage = (
  t,
  storeItemAvailability
): {
  text: string
  subText?: string[]
} => {
  const {
    selectedStore,
    isUnavailableAtSelectedStore,
    isUnavailableAtAllStores,
    isUnavailableAtSomeStores,
    availableStores,
    isPickup,
    unavailableStores
  } = storeItemAvailability
  const availableStoresName = availableStores
    .filter((store) => store.open)
    .map((store) => store?.name)
  const unavailableStoresName = unavailableStores
    .filter((store) => store.open)
    .map((store) => store?.name)

  let result = null

  if (isUnavailableAtSelectedStore && selectedStore) {
    result =
      availableStoresName.length === 0
        ? {
            text: t('Sorry, this item is currently out of stock in all stores.')
          }
        : isPickup
        ? {
            text: t(
              'Sorry, this item is currently out of stock at _storeName_. It is currently available at the following outlets:',
              {
                storeName: selectedStore.name
              }
            ),
            subText: availableStoresName
          }
        : {
            text: t('Sorry, this item is currently out of stock.')
          }
  }
  // this block will be fired when customer access menu at the first time
  if (isUnavailableAtSomeStores && !selectedStore) {
    result =
      unavailableStoresName.length > 0 && availableStoresName.length > 0
        ? {
            text: t(
              'Item subject to store availability. It is currently not available at the following outlets:'
            ),
            subText: unavailableStoresName
          }
        : availableStoresName.length === 0
        ? {
            text: t('Sorry, this item is currently out of stock in all stores.')
          }
        : null
  }

  if (isUnavailableAtAllStores) {
    result = {
      text: t('Sorry, this item is currently out of stock in all stores.')
    }
  }

  return result
}

const InlineNotificationText = React.memo(
  ({ text, subText }: { text: string; subText?: string[] }): JSX.Element => {
    const { t } = useContext(TranslationContext)
    const [showSubText, setShowSubText] = useState(false)

    if (!subText)
      return (
        <InlineNotification
          lightTheme
          text={text}
          state={notificationStates.informative}
        />
      )

    if (subText?.length <= 1) {
      return (
        <InlineNotification
          lightTheme
          text={text}
          state={notificationStates.informative}
          subText={subText.map((text) => (
            <ItemList>{text}</ItemList>
          ))}
        />
      )
    }

    const handleClick = () => {
      setShowSubText(!showSubText)
    }

    return (
      <InlineNotification
        lightTheme
        text={text}
        state={notificationStates.informative}
        subText={
          <>
            {showSubText
              ? subText.map((text, index) => (
                  <ItemList key={index}>{text}</ItemList>
                ))
              : subText
                  .filter((text, index) => index <= 1)
                  .map((text, index) => (
                    <ItemList key={index}>{text}</ItemList>
                  ))}
            {subText.length > 2 && (
              <ItemListClickable onClick={handleClick}>
                {showSubText ? t('Show less') : t('Show more')}
              </ItemListClickable>
            )}
          </>
        }
      />
    )
  }
)

interface ProductDescriptionsProps {
  productState: Menu.MenuItem
  showImage: boolean
  category: Menu.MenuData
}

const ProductDescriptions = React.memo(
  ({
    showImage,
    category,
    productState: product
  }: ProductDescriptionsProps): JSX.Element => {
    const router = useRouter()
    const { t } = useContext(TranslationContext)
    const message =
      getStoreItemAvailabilityMessage(
        t,
        useStoreItemAvailability({ product, category })
      ) || getSeasonalAvailabilityMessage(t, category, product, router)
    const { formatMoney } = useAppContext()
    const { preposition, displayPrice } = getDisplayPriceFromItem(
      t,
      product,
      formatMoney
    )
    const { previewMode } = useCustomisation()
    const { getProductImagePath } = useAppContext()
    const productNutrigradeTags = getAllProductNutrigradeTags(product.tags)

    const imageFullUrls =
      product.imageFullUrls?.filter((image) => !isEmpty(image))?.length > 0
        ? product.imageFullUrls
        : product.imageUrls?.filter((image) => !isEmpty(image))?.length > 0
        ? product.imageUrls
        : [null]
    const allImages = imageFullUrls.map((imageUrl) =>
      getProductImagePath(imageUrl, null, {
        shouldCache: true,
        containerHeight: 1000
      })
    )

    const images = isBrowser() ? allImages : [allImages[0]]

    return (
      <Container showImage={showImage}>
        <StyledProductName>{product.name}</StyledProductName>
        {(!showImage || !images[0]) && (product?.tags?.length || 0) > 0 && (
          <>
            <br />
            <NutriGradeIcon tags={product.tags} height={50} width={89} />
          </>
        )}
        {productNutrigradeTags && (
          <ProductTagContainer>
            {productNutrigradeTags?.map((tag) => (
              <ProductTag key={tag}>{tag}</ProductTag>
            ))}
          </ProductTagContainer>
        )}
        {message && (
          <InlineNotificationText
            text={message.text}
            subText={message.subText}
          />
        )}
        <ExternalContent previewMode={previewMode}>
          <StyledProductDescription
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(product.description)
            }}
          />
        </ExternalContent>
        <H4>{`${preposition} ${displayPrice}`}</H4>
        <SocialMediaSharing widgetId={ADDTHIS_PUBLIC_ID} />
      </Container>
    )
  }
)

export default ProductDescriptions
